import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskLine, Magnetic } from "../site/Motion";
import { LogoMark } from "../site/Logo";
import { waLink } from "../../lib/site";

const EASE = [0.16, 1, 0.3, 1];

const Badge = () => (
  <div className="absolute -left-10 bottom-10 sm:-left-14 w-28 h-28 sm:w-36 sm:h-36 z-20 rounded-full bg-cream p-2.5 shadow-[0_20px_50px_rgba(44,64,46,0.14)]">
    <img src="/img/mark-a.png" alt="Logo Annette" className="w-full h-full" />
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 70, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 70, damping: 16 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-cream grain">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pt-32 sm:pt-36 lg:pt-40 pb-16 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }} className="font-mono text-xs uppercase tracking-[0.3em] text-terra">
            Botanical shop · Floral designer wedding & event in Puglia
          </motion.p>
          <h1 data-testid="hero-title" className="mt-6 font-serif text-[3.3rem] leading-[0.92] sm:text-8xl lg:text-[6.4rem] xl:text-[7.4rem] tracking-[-0.02em] text-ink">
            <MaskLine delay={0.2}>Fiori che</MaskLine>
            <MaskLine delay={0.34}><span className="pl-[0.6em]">raccontano</span></MaskLine>
            <MaskLine delay={0.48}><em className="text-cypress">la vostra</em> <em className="text-terra">storia.</em></MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: EASE }} className="mt-8 max-w-md text-base sm:text-lg text-ink/75 leading-relaxed">
            Bouquet e composizioni creati ogni giorno in negozio, e allestimenti su misura per matrimoni che profumano di sud.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 1, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-4">
            <Magnetic>
              <Link to="/portfolio" data-testid="hero-portfolio-cta" className="inline-flex items-center gap-3 rounded-full bg-cypress text-cream pl-7 pr-2 py-2 text-sm hover:bg-terra transition-colors duration-300">
                Scopri il portfolio
                <span className="w-10 h-10 rounded-full bg-cream text-cypress flex items-center justify-center"><ArrowUpRight className="w-4 h-4" /></span>
              </Link>
            </Magnetic>
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="whatsapp-hero-cta" className="link-underline text-sm text-ink">Scrivimi su WhatsApp</a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative mx-auto w-full max-w-[520px]">
            <div className="relative rounded-t-[999px] overflow-hidden aspect-[4/5] lg:aspect-[3/4] shadow-[0_40px_80px_rgba(44,64,46,0.18)]">
              <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.6, ease: EASE, delay: 0.3 }} className="absolute inset-0">
                <motion.img src="/img/hero-bouquet.jpg" alt="Tavola nuziale fiorita" style={{ y, scale: 1.18 }} className="w-full h-full object-cover" />
              </motion.div>
            </div>
            <motion.div style={{ translateZ: 60 }} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.3, duration: 0.9, ease: EASE }}>
              <Badge />
            </motion.div>
            <motion.div style={{ translateZ: 40 }} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.5, duration: 0.9 }} className="absolute -right-2 sm:-right-6 top-10 bg-cypress text-cream px-4 py-3 font-mono text-[11px] tracking-[0.2em] uppercase">
              Dal negozio all'altare
            </motion.div>
          </motion.div>
        </div>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="hidden lg:flex absolute bottom-8 left-12 items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-ash">
        <ArrowDown className="w-4 h-4 animate-bounce" /> Scorri
      </motion.div>
    </section>
  );
};
